/**
 * The composer's live audience estimate: how many people this targeting
 * actually reaches, recomputed as the author edits the audience — and never
 * while they are typing the title.
 *
 * Hand-rolled useState/useEffect, matching `useBroadcastList.js` and the rest
 * of the comms surface. The fetch idiom is the same one: a cancelled flag owned
 * by the effect plus a generation counter in a ref, combined into a single
 * `isStale()` gate that a response has to pass before it is believed.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE OWNS AND WHAT IT DOES NOT
 * ---------------------------------------------------------------------------
 * It owns *when* to ask and *what a failure means*. It owns no wire names — the
 * request shape comes from `broadcastPayload.js`, the response shape from
 * `broadcastDecode.js`, and the copy for a failure from `broadcastErrors.js`.
 * Callers receive the camelCase estimate and a human string, never a raw
 * payload and never an `err.message`.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THIS HOOK EXISTS TO PROTECT
 * ---------------------------------------------------------------------------
 * A FAILED ESTIMATE YIELDS `null`, NEVER ZEROS.
 *
 * The send footer reads `estimate` to label its button. With `null` it reads
 * "Send". With a zero it reads "Send to 0", which tells the author "this will
 * reach nobody" when the truth is "we do not know" — and someone who believes
 * the first will go back and rebuild an audience that was never broken. So a
 * rejection sets `estimate` to null and `failed` to true, and nothing anywhere
 * below substitutes a number for an unknown.
 *
 * A genuine zero from the server is a DIFFERENT state and must stay
 * distinguishable: `estimate` is a real object whose `totalSends` is 0, and
 * `failed` is false. That pair — unknown vs. measured-empty — is the whole
 * reason this is a hook and not three lines in the composer.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { post as commsPost } from "../../../service/comms/commsApi";
import { estimateBroadcast } from "../../../service/comms/broadcastsService";
import { useCompanyId } from "../../../hooks/comms/useCompanyId";
import { estimateFromPayload } from "./broadcastDecode";
import { ESTIMATE_DEBOUNCE_MS } from "./broadcastEnums";
import { messageFor } from "./broadcastErrors";
import { buildEstimatePayload } from "./broadcastPayload";

/**
 * The `post` verb resolves `{ data, status }` where `get` resolves the body.
 * The decoder wants the body, so unwrap here rather than teaching every
 * decoder about an envelope only half the verbs produce.
 *
 * @param {Object} response
 * @returns {Object}
 */
const bodyOf = (response) =>
  response?.data !== undefined && response?.data !== null ? response.data : response;

/**
 * Estimate the reachable audience for a draft.
 *
 * @param {import("./broadcastDraft").BroadcastDraft} draft
 * @returns {{
 *   estimate: Object|null,
 *   estimating: boolean,
 *   failed: boolean,
 *   error: string|null,
 *   retry: () => void,
 * }}
 */
export default function useBroadcastEstimate(draft) {
  const { companyId, loading: companyLoading } = useCompanyId();

  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [error, setError] = useState(null);

  /** Bumped by `retry()`. A counter so two retries in a row both land. */
  const [retryToken, setRetryToken] = useState(0);

  /**
   * The generation of the most recently STARTED request. Paired with the
   * effect's own `cancelled` flag: the flag says "my effect was replaced", the
   * ref says "someone newer is authoritative now". A late response has to fail
   * neither check, so neither one quietly becomes load-bearing alone.
   */
  const generationRef = useRef(0);

  /** Set by `retry()` so the next effect run skips the debounce. */
  const immediateRef = useRef(false);

  /*
   * ---------------------------------------------------------------------
   * KEYED ON TARGETING ONLY — the single most important line in this file.
   * ---------------------------------------------------------------------
   * The estimate depends on the channels and the two segments, and on nothing
   * else in the draft. `draft` itself is a NEW OBJECT on every keystroke,
   * because the composer replaces it immutably; depending on it would fire one
   * request per character typed into the title, each one asking the server the
   * identical question. Reducing the three fields that matter to a primitive
   * string gives the effect a dependency that only changes when the answer
   * could change.
   */
  const channels = draft?.channels;
  const pushSegment = draft?.pushSegment;
  const emailSegment = draft?.emailSegment;
  const targetingKey = `${channels}|${pushSegment}|${emailSegment}`;

  useEffect(() => {
    /*
     * Gate on the company id. Asking with a null one is a documented prior
     * bug: the backend answered with an empty audience rather than an error,
     * so every segment estimated to zero and the composer confidently offered
     * to send to nobody. While the id is resolving this is a PENDING estimate,
     * which `estimating` already means — a second flag would only give the
     * footer another state to get wrong.
     */
    if (companyId === null) {
      setEstimating(companyLoading);
      return undefined;
    }

    let cancelled = false;
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    /** A response is worth believing only if it is both live and current. */
    const isStale = () => cancelled || generationRef.current !== generation;

    setEstimating(true);
    // Clear both up front. A count computed for a different audience is worse
    // than no count, and `failed` describes the LAST attempt — the one now
    // starting, not the one before it.
    setEstimate(null);
    setError(null);

    const fire = () => {
      // Only the three targeting fields are handed over, so this cannot start
      // depending on the rest of the draft by accident.
      const payload = buildEstimatePayload(
        { channels, pushSegment, emailSegment },
        { companyId }
      );

      estimateBroadcast(commsPost, payload)
        .then((response) => {
          if (isStale()) return;
          setEstimate(estimateFromPayload(bodyOf(response)));
          setError(null);
          setEstimating(false);
        })
        .catch((err) => {
          if (isStale()) return;
          // null, not zeros. See the header.
          setEstimate(null);
          // Never `err.message` — `messageFor` is the only place that decides
          // what a status code says to a person.
          setError(messageFor(err));
          setEstimating(false);
        });
    };

    // `retry()` is a deliberate click on a failed estimate, not a burst of
    // segment changes: making the author wait out a debounce for it would read
    // as the button doing nothing.
    if (immediateRef.current) {
      immediateRef.current = false;
      fire();
      return () => {
        cancelled = true;
      };
    }

    // Segment pills are clicked in bursts while the author compares audiences.
    // One request for the burst, carrying the selection they settled on.
    const timer = setTimeout(fire, ESTIMATE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `targetingKey` stands in for the three primitives read inside; they are
    // its only components, so it cannot go stale relative to them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetingKey, companyId, companyLoading, retryToken]);

  const retry = useCallback(() => {
    immediateRef.current = true;
    setRetryToken((token) => token + 1);
  }, []);

  return {
    estimate,
    estimating,
    /** Derived, not stored: two sources for one fact is how they disagree. */
    failed: error !== null,
    error,
    retry,
  };
}
